<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>Triggered by</form-label>
            <p v-if="!triggers.length" class="text-xs text-gray-300">
                Connect an event (or a condition) to this action; values such as <code>{val}</code> depend on that event.
            </p>
            <form-select v-else-if="triggers.length > 1" name="Edit in the context of"
                :model-value="activeTriggerId" @update:model-value="id => (chosenTriggerId = id)">
                <option v-for="trigger in triggers" :key="trigger.id" :value="trigger.id">{{ trigger.label }}</option>
            </form-select>
            <p v-else class="text-xs text-gray-200">{{ triggers[0].label }}</p>
        </section>

        <!-- Command categories choose their type in the command editor below. -->
        <form-select v-if="variantChoices" name="Type" :model-value="variantId" @update:model-value="changeVariant">
            <option v-for="variant in variantChoices" :key="variant.id" :value="variant.id">{{ variant.label }}</option>
        </form-select>

        <ActionEditorRouter :node="editorNode" :handler="handler" :action="draft" :handlers="handlers"
            :command-categories="category?.commandCategory ? [category.commandCategory] : null"
            :targets="targets" :template-targets="templateTargets"
            :settings="settings" :gpio-metadata="gpioMetadata" :nested="nested" @change="commit" />
        <p v-if="error" class="text-xs italic text-red-300">{{ error }}</p>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useLogicStore } from '../../stores/logicStore.js';
import { findNode, getEventActionWires, getEventSourceRef, getRootEvents, isConditional } from '../../logic/logicGraph.js';
import { gpioEditorSource } from '../../logic/gpioLogic.js';
import { compileLogicGraph, mergeObjectActionHandlers } from '../../logic/logicCompiler.js';
import { getActionCategory, getActionCategoryId, getActionVariantId } from '../../Hasp/properties/v2/logic/index.js';
import { validateActionInContext } from '../../actions/actionValidation.js';
import ActionEditorRouter from '../properties/actions/ActionEditorRouter.vue';
import { useActionEditorContext } from '../properties/actions/useActionEditorContext.js';

const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
    settings: { type: Object, default: () => ({}) },
});
const logic = useLogicStore();

// Every event node that runs this action, directly or through conditions and
// switches, with its source: a UI object, the page, or a GPIO entity node
// (presented to the editors as a stand-in).
const sourceName = (object, gpio) => {
    if (gpio) return `GPIO ${gpio.data.entity || '(no entity)'}`;
    if (object.obj === 'page') return `page p${object.haspid}`;
    return `${object.obj} p${props.page.haspid}b${object.haspid}`;
};
const triggers = computed(() => getRootEvents(props.graph, props.node.id)
    .map(event => {
        const ref = getEventSourceRef(props.graph, event.id);
        const gpio = ref?.node !== undefined ? findNode(props.graph, ref.node) : null;
        const objectUuid = ref?.object ?? null;
        const object = gpio ? gpioEditorSource(props.page, gpio) : objectUuid ? props.context.getObject(objectUuid) : null;
        const label = object
            ? `${event.data.event || '(no event)'} on ${sourceName(object, gpio)}`
            : `${event.data.event || '(no event)'} — no source`;
        return { id: event.id, event, objectUuid, gpioEntity: gpio?.data.entity ?? null, object, label };
    }));
const nested = computed(() => isConditional(props.graph, props.node.id));
const chosenTriggerId = ref(null);
const activeTrigger = computed(() => triggers.value.find(item => item.id === chosenTriggerId.value) ?? triggers.value[0] ?? null);
const activeTriggerId = computed(() => activeTrigger.value?.id ?? null);

// Without a source object the editors still need a node for page/layer
// lookups; the page itself has no event values, so nothing invalid is offered.
const editorNode = computed(() => activeTrigger.value?.object ?? props.page);

const withoutId = action => {
    const data = JSON.parse(JSON.stringify(action));
    delete data.id;
    return data;
};
// The editors mutate the action in place; this draft is committed on change.
const draft = ref({ ...structuredClone(props.node.data), id: props.node.id });
watch(() => JSON.stringify(props.node.data), json => {
    if (json !== JSON.stringify(withoutId(draft.value))) draft.value = { ...JSON.parse(json), id: props.node.id };
});

const handler = computed(() => {
    const trigger = activeTrigger.value;
    if (!trigger) return { id: '', event: '', actions: [draft.value] };
    const actions = getEventActionWires(props.graph, trigger.id)
        .map(wire => {
            if (wire.to.node === props.node.id) return draft.value;
            const sibling = findNode(props.graph, wire.to.node);
            return sibling ? { ...sibling.data, id: sibling.id } : null;
        })
        .filter(Boolean);
    return { id: trigger.id, event: trigger.event.data.event ?? '', actions };
});
// All handlers of the source object as they will export: stored actions plus
// compiled logic, with this event's handler replaced by the live one.
const handlers = computed(() => {
    const trigger = activeTrigger.value;
    if (!trigger?.objectUuid && !trigger?.gpioEntity) return [handler.value];
    const compiled = compileLogicGraph(props.graph, props.context);
    const existing = (trigger.objectUuid
        ? compiled.handlers.get(trigger.objectUuid)
        : compiled.gpioHandlers.get(trigger.gpioEntity)) ?? [];
    return mergeObjectActionHandlers(trigger.object.actions ?? [],
        [...existing.filter(item => item.id !== trigger.id), handler.value]);
});

// The node's category; its type within the category is chosen here (or, for
// command categories, in the command editor restricted to that category).
const category = computed(() => getActionCategory(getActionCategoryId(props.node)));
const variantChoices = computed(() => (category.value && !category.value.commandCategory &&
    category.value.variants.length > 1 ? category.value.variants : null));
const variantId = computed(() => getActionVariantId(props.node));
function changeVariant(id) {
    logic.setNodeVariant(props.page, props.node.id, category.value.variants.find(variant => variant.id === id));
}

const { targets, templateTargets, gpioMetadata } = useActionEditorContext(() => editorNode.value, () => props.settings);

const error = computed(() => (activeTrigger.value?.object
    ? validateActionInContext({
        action: draft.value, node: editorNode.value, handler: handler.value,
        handlers: handlers.value, gpioMetadata: gpioMetadata.value, nested: nested.value,
    })
    : ''));

function commit() {
    logic.setNodeData(props.page, props.node.id, withoutId(draft.value));
}
</script>
